import { useState } from "react";
import AppShell from "@/components/AppShell";
import { toast } from "sonner";
import { DownloadSimple, Browsers, Lightning, ShieldCheck, Sparkle, CheckCircle, ArrowsClockwise, Copy, GoogleChromeLogo, Star, Warning } from "@phosphor-icons/react";

const PORTALS = [
  "Workday", "Greenhouse", "Lever", "SmartRecruiters",
  "iCIMS", "Ashby", "BambooHR", "+ generic forms",
];

const FAQ = [
  {
    q: "Does it work on every job site?",
    a: "Most. We have proper, tested support for Workday, Greenhouse, Lever, SmartRecruiters, iCIMS, Ashby, and BambooHR. On other sites we fall back to label-matching heuristics — usually fills name / email / phone / LinkedIn / location automatically, and you fill anything weird in a few seconds.",
  },
  {
    q: "Is my data stored on Google's servers?",
    a: "No. The extension caches your vault locally in your browser via chrome.storage. It only talks to JobVault when you log in or click \"sync vault\". When you log out, the local cache is wiped.",
  },
  {
    q: "Can the extension see my passwords?",
    a: "No. It uses session cookies set by JobVault to authenticate to your backend, and it never reads form values on pages — only writes them. Open-source-style: every line of code is in /app/extension/ if you want to audit.",
  },
  {
    q: "Why does it ask for the backend URL?",
    a: "Defaults to jobvault.co.uk — the production domain. You can override it for testing against a preview/staging environment, but most users never need to touch it.",
  },
  {
    q: "What if it fills the wrong field?",
    a: "Click the field, retype the value, and re-submit. The extension never overrides a non-empty field, so anything you type takes precedence.",
  },
  {
    q: "Will it break if a portal updates their HTML?",
    a: "Workday and Greenhouse rarely change their stable selectors (data-automation-id / form names), so portal-specific autofill is robust. Smaller portals can drift — if something breaks, message us with the URL and we'll patch it.",
  },
];

const STEPS = [
  { n: "1", title: "download the zip", body: "click the button below — it's the latest build (v1.0.0)." },
  { n: "2", title: "unzip it somewhere", body: "doesn't matter where — desktop, downloads folder, anywhere chrome can read." },
  { n: "3", title: "open chrome://extensions", body: "paste that into a new tab. toggle \"developer mode\" on (top right)." },
  { n: "4", title: "load unpacked", body: "click \"load unpacked\" → pick the unzipped folder. JobVault appears in your toolbar." },
  { n: "5", title: "log in once", body: "click the toolbar icon, enter your JobVault URL + email + password. that's it." },
  { n: "6", title: "find a job ad", body: "open any application page. click the JobVault icon → \"autofill this page\". watch the fields fill themselves." },
];

export default function Autofill() {
  const [copied, setCopied] = useState(false);

  const copyChromeUrl = async () => {
    try {
      await navigator.clipboard.writeText("chrome://extensions");
      setCopied(true);
      toast.success("Copied — paste into a new Chrome tab");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Copy failed");
    }
  };

  return (
    <AppShell title="Browser Extension">
      <p className="text-[var(--brand-muted)] max-w-2xl mb-6">
        the magic bit. one click on any job ad — autofill, doc upload, instant culture-fit star rating. saves 4 minutes per application. minimum.
      </p>

      {/* Hero / download */}
      <div className="card-hard rounded-md p-6 lg:p-8 bg-[var(--brand-ink)] text-white mb-6" data-testid="autofill-hero">
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
          <div>
            <p className="label-tiny text-[var(--bolt-sage)] mb-2 flex items-center gap-1.5">
              <Browsers size={12} weight="duotone"/> /chrome extension
            </p>
            <h2 className="font-display text-4xl font-semibold tracking-tight text-white">
              never fill a form <span className="text-[var(--bolt-sage)]">twice.</span>
            </h2>
            <p className="text-white/70 mt-2 max-w-md">
              install once, autofill forever. works on Workday, Greenhouse, Lever &amp; 4 more — plus heuristic fallback for everything else.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <a href="/vault-extension.zip" download data-testid="autofill-download"
              className="btn-hard bg-[var(--bolt-sage)] text-[var(--brand-ink)] rounded-md px-5 py-3 text-sm font-semibold flex items-center gap-2">
              <DownloadSimple size={18} weight="bold"/> download extension (v1.0.0)
            </a>
            <span className="font-mono-d text-xs text-white/50">~12 KB · MV3 · Chrome / Edge / Brave</span>
          </div>
        </div>
      </div>

      {/* What it does */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="card-hard rounded-md p-5 bg-[var(--pastel-mint)]" data-testid="feature-autofill">
          <p className="label-tiny mb-2 flex items-center gap-1.5"><Lightning size={12} weight="duotone"/> /autofill</p>
          <h3 className="font-display text-xl tracking-tight">smart field detection.</h3>
          <p className="text-sm text-[var(--brand-muted)] mt-2">
            uses portal-specific selectors first (rock-solid for Workday &amp; Greenhouse), label heuristics second. fills 10-30 fields in ~1 second.
          </p>
        </div>
        <div className="card-hard rounded-md p-5 bg-[var(--pastel-yellow)]" data-testid="feature-doc-upload">
          <p className="label-tiny mb-2 flex items-center gap-1.5"><DownloadSimple size={12} weight="duotone"/> /document upload</p>
          <h3 className="font-display text-xl tracking-tight">attach CV in one click.</h3>
          <p className="text-sm text-[var(--brand-muted)] mt-2">
            pick a doc from your vault. extension finds the right file input (smart enough to know &ldquo;CV&rdquo; from &ldquo;Other&rdquo;) and attaches it.
          </p>
        </div>
        <div className="card-hard rounded-md p-5 bg-[var(--pastel-peach)]" data-testid="feature-health-check">
          <p className="label-tiny mb-2 flex items-center gap-1.5"><Star size={12} weight="duotone"/> /job health check</p>
          <h3 className="font-display text-xl tracking-tight">star-rate any job ad.</h3>
          <p className="text-sm text-[var(--brand-muted)] mt-2">
            click the magic wand on any listing → AI scrapes the page → instant star rating + red flags. saved to your Compare tab.
          </p>
        </div>
      </div>

      {/* Supported portals */}
      <div className="card-hard rounded-md p-5 bg-white mb-6" data-testid="portals-card">
        <p className="label-tiny mb-3 flex items-center gap-1.5"><ShieldCheck size={12} weight="duotone"/> /tested portals</p>
        <div className="flex flex-wrap gap-2">
          {PORTALS.map((p) => (
            <span key={p} className="card-hard rounded-full px-3 py-1.5 text-xs bg-[var(--brand-bg)]">{p}</span>
          ))}
        </div>
        <p className="font-mono-d text-xs text-[var(--brand-muted)] mt-4">
          missing a portal you use? message us — we add support in ~24 hours.
        </p>
      </div>

      {/* Install steps */}
      <div className="mb-6" data-testid="install-steps">
        <p className="label-tiny mb-3">/how to install (~90 seconds)</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {STEPS.map((s) => (
            <div key={s.n} className="card-hard rounded-md p-4 bg-white" data-testid={`step-${s.n}`}>
              <div className="flex items-start gap-3">
                <div className="card-hard rounded-md w-8 h-8 flex items-center justify-center font-display font-bold bg-[var(--brand-ink)] text-white shrink-0">
                  {s.n}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium">{s.title}</p>
                  <p className="text-xs text-[var(--brand-muted)] mt-1">{s.body}</p>
                  {s.n === "3" && (
                    <button onClick={copyChromeUrl} data-testid="copy-chrome-url"
                      className="mt-2 btn-hard bg-[var(--pastel-yellow)] rounded-md px-2.5 py-1 text-xs flex items-center gap-1.5">
                      {copied ? <CheckCircle size={12} weight="duotone"/> : <Copy size={12}/>}
                      {copied ? "copied" : "copy chrome://extensions"}
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Coming soon: Web Store */}
      <div className="card-hard rounded-md p-5 bg-[var(--pastel-yellow)] mb-6" data-testid="coming-soon">
        <p className="label-tiny mb-2 flex items-center gap-1.5"><GoogleChromeLogo size={12} weight="duotone"/> /coming soon</p>
        <h3 className="font-display text-xl tracking-tight">one-click install from Chrome Web Store.</h3>
        <p className="text-sm text-[var(--brand-muted)] mt-2">
          we&apos;re polishing the listing — once approved (1-3 days from Google) you&apos;ll be able to install with a single click. for now, the unpacked install above works exactly the same.
        </p>
      </div>

      {/* FAQ */}
      <div data-testid="autofill-faq">
        <p className="label-tiny mb-3">/questions</p>
        <div className="space-y-2">
          {FAQ.map((item, i) => (
            <details key={i} className="card-hard rounded-md bg-white" data-testid={`faq-${i}`}>
              <summary className="cursor-pointer p-4 text-sm font-medium flex items-center justify-between">
                {item.q}
                <span className="font-mono-d text-xs text-[var(--brand-muted)]">+</span>
              </summary>
              <p className="px-4 pb-4 text-sm text-[var(--brand-muted)] leading-relaxed">{item.a}</p>
            </details>
          ))}
        </div>
      </div>

      <p className="font-mono-d text-xs text-[var(--brand-muted)] mt-8 text-center">
        broken? edge case? <a href="mailto:jobvaultadmin@gmail.com" className="underline">message us</a> — a human reads it.
      </p>
    </AppShell>
  );
}
